import { useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  Accordion,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Text,
} from '@kula-ai/components';
import {
  ChevronDown,
  DoorOpen,
  LayersThree02,
  Users01,
} from '@kula-ai/icons-core';
import { Box, Flex } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import type { RecipientOption, RecipientSection } from './useRecipientSections';

interface SectionChrome {
  Icon: ComponentType<{ size?: number; color?: string }>;
}

const SECTION_CHROME: Record<RecipientSection['id'], SectionChrome> = {
  job_roles: { Icon: LayersThree02 },
  kula_roles: { Icon: DoorOpen },
  users: { Icon: Users01 },
};

const SECTION_ORDER: RecipientSection['id'][] = [
  'job_roles',
  'kula_roles',
  'users',
];

export interface RecipientPickerConfig {
  sections: RecipientSection[];
  /** Every option the picker has rendered, so a chip keeps its label. */
  optionByValue: Map<string, { value: string; label: string; image?: string }>;
}

/**
 * react-select passes these to every custom component. Typed structurally on
 * purpose: kula's `Select` runs react-select **5**, while core-web's own
 * dependency is **4** — importing `MenuProps` here would type against v4 and
 * compile clean while misbehaving at runtime.
 */
interface RecipientMenuProps {
  innerProps?: Record<string, unknown>;
  /** react-select's processed options — kula-wrapped, so they carry `__original`. */
  options?: ReadonlyArray<{ value?: unknown }>;
  getValue: () => ReadonlyArray<{ value?: unknown }>;
  setValue: (_value: unknown, _action: string) => void;
  selectProps: {
    inputValue?: string;
    onMenuClose?: () => void;
    recipientPicker?: RecipientPickerConfig;
  };
}

/**
 * Role rows are 44px with an 18px initials chip; user rows are 52px with a 24px
 * avatar and a second line. Both share the 12px gutter and 6px column gaps.
 */
const SectionRow = ({
  option,
  checked,
  onToggle,
}: {
  option: RecipientOption;
  checked: boolean;
  onToggle: () => void;
}) => {
  const isRole = Boolean(option.initials);
  const isDisabled = Boolean(option.disabled);

  return (
    <Flex
      as="button"
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={isDisabled || undefined}
      // Deliberately NOT the `disabled` attribute. A disabled <button> has its
      // mouse events suppressed by the browser, so the press never dispatches
      // and never reaches the menu's `onMouseDown` — focus still leaves
      // react-select's input and it closes the menu on blur. `aria-disabled`
      // plus no `onClick` conveys and enforces the same thing, while letting
      // the press through so focus can be preserved.
      tabIndex={isDisabled ? -1 : undefined}
      align="center"
      gap="6px"
      px="12px"
      h={isRole ? '44px' : '52px'}
      w="100%"
      textAlign="left"
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.5 : 1}
      borderBottom="1px solid"
      borderColor="secondary-100"
      _hover={isDisabled ? undefined : { bg: 'secondary-25' }}
      onMouseDown={(event) => event.preventDefault()}
      onClick={isDisabled ? undefined : onToggle}
      data-test-id={`recipient-option-${option.value}`}
    >
      {/* The row owns the click; the box is the indicator, so a tick and a
          row click cannot both fire and cancel each other out. */}
      <Box pointerEvents="none" display="flex">
        <Checkbox size="sm" checked={checked} readOnly />
      </Box>

      {isRole ? (
        <Flex
          align="center"
          justify="center"
          minW="18px"
          h="18px"
          px="4px"
          flexShrink={0}
          borderRadius="4px"
          // `pastel-3-light`, not the `pastel-3light` spelling the raw
          // design-tokens package uses — Chakra cannot resolve that one and
          // silently emits no background at all.
          bg="pastel-3-light"
          data-test-id="recipient-option-initials"
        >
          {/* kula Text bottoms out at 12px (span/small/caption) and these
              initials are 10px, so this one label stays local. */}
          <Box as="span" fontSize="10px" fontWeight={650} color="base-black">
            {option.initials}
          </Box>
        </Flex>
      ) : (
        <Avatar size={24} src={option.image} name={option.label} />
      )}

      <Box flex="1" minW={0}>
        <Text variant={isRole ? 'p' : 'h4'} truncate>
          {option.label}
        </Text>
        {option.email && (
          <Text variant="p" truncate>
            {option.email}
          </Text>
        )}
      </Box>

      {option.hint ? (
        <Text
          variant="p"
          color="muted"
          style={{ flexShrink: 0 }}
          data-test-id="recipient-option-hint"
        >
          {option.hint}
        </Text>
      ) : (
        option.tag && <Badge color="grey">{option.tag}</Badge>
      )}
    </Flex>
  );
};

/**
 * The To/Cc/Bcc dropdown: job roles, Kula roles and users in three accordions.
 *
 * Rendered as react-select's `Menu` through `reactSelectProps`, so kula's own
 * chips, placeholder, error display and card chrome all stay — only the panel
 * is ours. Selection is staged locally and committed on Add, which react-select
 * has no notion of; `setValue` is what makes that possible.
 *
 * Search is react-select's own input (`selectProps.inputValue`): the two role
 * sections filter in memory, and the caller re-queries users on the same term.
 */
export const RecipientPickerMenu = (props: RecipientMenuProps): ReactNode => {
  const { selectProps, getValue, setValue, innerProps, options } = props;
  const { recipientPicker, onMenuClose } = selectProps;
  const sections = recipientPicker?.sections ?? [];

  const committed = useMemo(
    () => (getValue() ?? []).map((option) => String(option?.value ?? '')),
    [getValue]
  );

  // Seeded once per open — the menu unmounts on close, so Cancel is simply
  // "never commit", with no reset to write.
  const [staged, setStaged] = useState<string[]>(committed);

  const toggle = (value: string) =>
    setStaged((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );

  const commit = () => {
    const byValue = recipientPicker?.optionByValue;
    // kula's `useSelectValue` maps every committed option to `__original` and
    // then reads `.value` off it — a plain `{value,label}` makes that throw, so
    // onChange never fires and Add silently does nothing. Prefer react-select's
    // own processed option (already wrapped); otherwise wrap it the same way.
    const wrapped = new Map(
      (options ?? []).map((option) => [String(option?.value ?? ''), option])
    );
    setValue(
      staged.map((value) => {
        const known = wrapped.get(value);
        if (known) return known;
        const original = byValue?.get(value) ?? { value, label: value };
        return { ...original, __original: original };
      }),
      'select-option'
    );
    onMenuClose?.();
  };

  const ordered = SECTION_ORDER.map((id) =>
    sections.find((section) => section.id === id)
  ).filter((section): section is RecipientSection => Boolean(section));

  return (
    <Box
      {...innerProps}
      // Keeps focus in react-select's input for every press inside the menu.
      // Without it a press that lands on menu chrome rather than an enabled row
      // — a disabled option (a disabled <button> fires no mouse events, so the
      // press hits this container), a section header, plain padding — blurs the
      // input, and react-select closes the menu on blur. `onClick` still fires,
      // so rows and the Cancel/Add buttons are unaffected.
      onMouseDown={(event) => event.preventDefault()}
      bg="white"
      borderRadius="6px"
      border="1px solid"
      borderColor="secondary-100"
      boxShadow="drop-shadows-bottom"
      overflow="hidden"
      data-test-id="recipient-picker-menu"
    >
      <Box maxH="360px" overflowY="auto">
        <Accordion.Root
          type="multiple"
          defaultValue={SECTION_ORDER}
          indicatorPosition="left"
          // A custom indicator opts out of kula's built-in chevron rotation, so
          // the open-state flip is re-declared here against our own class.
          indicator={
            <Flex
              align="center"
              justify="center"
              boxSize="52px"
              flexShrink={0}
              borderRight="1px solid"
              borderColor="secondary-100"
            >
              <ChevronDown
                className="recipient-chevron transition-transform duration-200"
                size={18}
                color={colors['secondary-700']}
              />
            </Flex>
          }
          triggerClassName="h-[52px] gap-0 p-0 bg-secondary-50 hover:bg-secondary-50 [&[data-state=open]_.recipient-chevron]:rotate-180"
          // Rows and headers draw their own separators, so the item wrapper's
          // would double up at every section boundary.
          itemClassName="border-b-0"
          contentClassName="[&>div]:pb-0"
          // Radix would otherwise steal the keystrokes from react-select's input.
          onKeyDown={(event) => event.stopPropagation()}
        >
          {ordered.map((section) => {
            const { Icon } = SECTION_CHROME[section.id];

            return (
              <Accordion.Item key={section.id} value={section.id}>
                <Accordion.Trigger
                  data-test-id={`recipient-section-${section.id}`}
                >
                  <Flex
                    align="center"
                    gap="6px"
                    pl="12px"
                    pr="12px"
                    w="100%"
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    h="52px"
                  >
                    <Icon size={18} color={colors['secondary-700']} />
                    <Flex align="center" gap="8px">
                      <Text variant="overhead" color="heading">
                        {section.title}
                      </Text>
                      <Badge type="outline" color="white">
                        {section.count}
                      </Badge>
                    </Flex>
                  </Flex>
                </Accordion.Trigger>
                <Accordion.Content>
                  {section.options.length === 0 ? (
                    <Text variant="p" style={{ padding: '12px' }}>
                      No matches
                    </Text>
                  ) : (
                    section.options.map((option) => (
                      <SectionRow
                        key={option.value}
                        option={option}
                        checked={
                          !option.disabled && staged.includes(option.value)
                        }
                        onToggle={() => toggle(option.value)}
                      />
                    ))
                  )}
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion.Root>
      </Box>

      <Flex
        justify="flex-end"
        gap="6px"
        p="12px"
        borderTop="1px solid"
        borderColor="secondary-100"
      >
        <Button
          variant="secondary"
          size="regular"
          onClick={() => onMenuClose?.()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="regular"
          onClick={commit}
          data-test-id="recipient-picker-add"
        >
          Add
        </Button>
      </Flex>
    </Box>
  );
};
